/**
 * Dialog used by the Phase detail panel to link the phase to one Backlog
 * category. A phase can hold multiple links — the caller passes the set
 * of already-linked entries via `existingLinkKeys` so we can grey out
 * duplicates in the picker.
 *
 * The category analogue of {@link PhaseBacklogLinkDialog}. Two-step picker:
 *   1. Choose a Backlog project (filtered to the project's configured keys).
 *   2. Choose a category within that project.
 *
 * The "連携する" button calls `linkPhaseToBacklogCategory`, which fans out
 * into per-issue WBS task creates. Until the callable resolves we surface
 * a busy spinner.
 *
 * Side effect: same `backlog.backlogProjectNames` background refresh as
 * the milestone dialog — when the snapshot is missing entries for one or
 * more configured keys we silently kick `listBacklogProjects` and write
 * the resolved names back so the picker can render `名前 (Key)`. Failure
 * is silent (falls back to the bare key).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatBacklogProjectLabel } from '@mct/shared';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { useProject } from '../../hooks/useFirestoreData';
import { useBacklogIssueMeta } from '../../hooks/useBacklogIssueMeta';
import {
  linkPhaseToBacklogCategory,
  type LinkPhaseToBacklogCategoryResult,
} from '../../lib/backlogCategoryLink';
import { listBacklogProjects } from '../../lib/adminCallables';
import { updateBacklogConfig } from '../../lib/firestore';
import { getDb } from '../../lib/firebase';

export function PhaseBacklogCategoryLinkDialog({
  open,
  projectId,
  phaseId,
  phaseName,
  existingLinkKeys,
  onClose,
  onLinked,
}: {
  open: boolean;
  projectId: string;
  phaseId: string;
  phaseName: string;
  /**
   * Set of `${projectKey}:${categoryId}` already linked on this phase.
   * Used to disable duplicate selections. Optional — omitted means "no
   * dupes to check".
   */
  existingLinkKeys?: ReadonlySet<string>;
  onClose: () => void;
  onLinked: (result: LinkPhaseToBacklogCategoryResult) => void;
}) {
  const [selectedProjectKey, setSelectedProjectKey] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Reset state every time the dialog opens so a previous selection
  // doesn't bleed across phases.
  useEffect(() => {
    if (open) {
      setSelectedProjectKey('');
      setSelectedCategoryId(null);
      setBusy(false);
      setErrorMsg(null);
    }
  }, [open, phaseId]);

  const { data: project } = useProject(open ? projectId : null);
  const backlogProjects = useMemo(() => {
    const keys = project?.backlog?.backlogProjectKeys ?? [];
    const names = project?.backlog?.backlogProjectNames ?? {};
    return keys
      .map((key) => ({ key, name: names[key] ?? '' }))
      .slice()
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [project]);

  // Background refresh of `backlog.backlogProjectNames` when the snapshot
  // is missing entries for any configured key (fire-and-forget; failure
  // is silent — UI just shows the bare key).
  const autoRefreshAttempted = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      autoRefreshAttempted.current = null;
      return;
    }
    if (!project || !project.backlog) return;
    const cfg = project.backlog;
    const keys = cfg.backlogProjectKeys ?? [];
    if (keys.length === 0) return;
    const names = cfg.backlogProjectNames ?? {};
    const missing = keys.some((k) => !(names[k] && names[k].trim().length > 0));
    if (!missing) return;
    const attemptKey = `${projectId}:${keys.slice().sort().join(',')}`;
    if (autoRefreshAttempted.current === attemptKey) return;
    autoRefreshAttempted.current = attemptKey;

    let cancelled = false;
    (async () => {
      try {
        const res = await listBacklogProjects(projectId);
        if (cancelled || !res.ok) return;
        const nameByKey: Record<string, string> = { ...names };
        for (const k of keys) {
          const live = res.projects.find((p) => p.projectKey === k);
          if (live && live.name && live.name.trim().length > 0) {
            nameByKey[k] = live.name;
          }
        }
        const changed = keys.some((k) => names[k] !== nameByKey[k]);
        if (!changed) return;
        await updateBacklogConfig(getDb(), projectId, {
          ...cfg,
          backlogProjectNames: nameByKey,
        });
      } catch {
        // silent — fall back to bare keys
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, project, projectId]);

  // Category master is cascade-narrowed by the chosen Backlog project so
  // we don't probe every configured key for nothing once the user picks one.
  const metaFilter = useMemo(
    () => (selectedProjectKey ? { backlogProjectKeys: [selectedProjectKey] } : undefined),
    [selectedProjectKey],
  );
  const { data: meta, loading: metaLoading } = useBacklogIssueMeta(
    open && selectedProjectKey ? projectId : null,
    metaFilter,
  );

  const categories = useMemo(() => {
    if (!meta || !meta.ok) return [];
    return meta.categories
      .filter((c) => !selectedProjectKey || c.backlogProjectKey === selectedProjectKey)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [meta, selectedProjectKey]);

  const isDuplicate = useMemo(() => {
    if (!selectedProjectKey || selectedCategoryId == null) return false;
    return (
      existingLinkKeys?.has(`${selectedProjectKey}:${selectedCategoryId}`) === true
    );
  }, [existingLinkKeys, selectedProjectKey, selectedCategoryId]);

  const canSubmit =
    !busy &&
    selectedProjectKey.length > 0 &&
    selectedCategoryId !== null &&
    !isDuplicate;

  const handleSubmit = async () => {
    if (!canSubmit || selectedCategoryId === null) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const result = await linkPhaseToBacklogCategory({
        projectId,
        phaseId,
        backlogProjectKey: selectedProjectKey,
        categoryId: selectedCategoryId,
      });
      if (!result.ok) {
        setErrorMsg(result.message);
        setBusy(false);
        return;
      }
      onLinked(result);
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={`「${phaseName}」と Backlog カテゴリを連携`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {busy ? (
              <span className="inline-flex items-center gap-1.5">
                <Spinner className="h-3.5 w-3.5" /> 取り込み中…
              </span>
            ) : (
              '連携する'
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-xs text-slate-500">
          選択したカテゴリの課題を、フェーズ直下の WBS タスクとして自動で取り込みます。
        </p>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Backlog プロジェクト
          </label>
          <select
            className="input"
            value={selectedProjectKey}
            disabled={busy}
            onChange={(e) => {
              setSelectedProjectKey(e.target.value);
              setSelectedCategoryId(null);
            }}
            data-testid="phase-category-link-project"
          >
            <option value="">— プロジェクトを選択 —</option>
            {backlogProjects.map((p) => (
              <option key={p.key} value={p.key}>
                {formatBacklogProjectLabel(p.key, p.name)}
              </option>
            ))}
          </select>
          {project && backlogProjects.length === 0 ? (
            <p className="mt-1 text-[11px] text-amber-700">
              連携プロジェクトが未設定です。プロジェクト設定で Backlog
              プロジェクトを 1 件以上選択してください。
            </p>
          ) : null}
          {meta && !meta.ok ? (
            <p className="mt-1 text-[11px] text-red-600">{meta.message}</p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            カテゴリー
          </label>
          <select
            className="input"
            value={selectedCategoryId == null ? '' : String(selectedCategoryId)}
            disabled={busy || !selectedProjectKey || metaLoading}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedCategoryId(v === '' ? null : Number(v));
            }}
            data-testid="phase-category-link-category"
          >
            <option value="">— カテゴリを選択 —</option>
            {categories.map((c) => {
              const alreadyLinked =
                existingLinkKeys?.has(`${selectedProjectKey}:${c.id}`) === true;
              return (
                <option key={c.id} value={String(c.id)} disabled={alreadyLinked}>
                  {c.name}
                  {alreadyLinked ? '（連携済み）' : ''}
                </option>
              );
            })}
          </select>
          {selectedProjectKey && !metaLoading && categories.length === 0 ? (
            <p className="mt-1 text-[11px] text-amber-700">
              このプロジェクトにはカテゴリが見つかりませんでした。Backlog
              側でカテゴリが設定されているか確認してください。
            </p>
          ) : null}
          {isDuplicate ? (
            <p className="mt-1 text-[11px] text-amber-700">
              そのカテゴリは既にこのフェーズに連携されています。
            </p>
          ) : null}
        </div>
        {errorMsg ? (
          <p className="text-[11px] text-red-600" data-testid="phase-category-link-error">
            {errorMsg}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
